import { Loader } from "shared/components/Loader";
import { EmptyPropertyGroups } from "./components/EmptyPropertyGroups";
import { PropertyGroupsTable } from "./components/PropertyGroupsTable";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { PROPERTY_GROUPS_QUERY_KEY } from "./constants";
import { useState } from "react";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useAuthContext } from "providers/Auth";
import { Layout } from "providers/Layout";
import { ButtonSize, ButtonVariant } from "shared/components/Button";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Show } from "shared/components/helpers/Show";
import { Button } from "shared/components/Button";
import { useNavigate } from "react-router-dom";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import ArrowSplit from "shared/icons/arrowSplit.svg?react";
import { RegroupWarningModal } from "./components/RegroupWarningModal";
import { QUERY_SETTINGS, getSettings } from "api/landlord/landlord";
import {
  getIntegrationCredentials,
  QUERY_INTEGRATION_CREDENTIALS
} from "api/landlord/integrations";
import { PropertyManagementSystem } from "shared/types/partner";

export const PropertyGroups = () => {
  const { type } = useAuthContext();
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();
  const { adminPrivileges } = type;
  const [searchParams, setSearchParams] = useState({
    search: ""
  });
  const [isRegroupModalOpen, setIsRegroupModalOpen] = useState(false);
  const [debouncedSearchTerm] = useDebouncedValue(searchParams.search, 500);

  const { data: config, isLoading: isLoadingConfig } = useQuery({
    queryKey: [PROPERTY_GROUPS_QUERY_KEY.CONFIG],
    queryFn: () =>
      instance.get("/portal/v1/property_groups/config").then((res) => res.data)
  });

  const { data: settingsData } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => getSettings()
  });

  const {
    data: integrationCredentialsData,
    isLoading: isLoadingIntegrationCredentials,
    isError: isIntegrationCredentialsError,
    refetch: refetchIntegrationCredentials
  } = useQuery({
    queryKey: [QUERY_INTEGRATION_CREDENTIALS],
    queryFn: getIntegrationCredentials
  });

  const { data: propertyGroups, isFetching: isFetchingPropertyGroups } =
    useQuery({
      queryKey: [
        PROPERTY_GROUPS_QUERY_KEY.PROPERTY_GROUPS,
        debouncedSearchTerm
      ],
      queryFn: () =>
        instance
          .get("/portal/v1/property_groups", {
            params: {
              search: debouncedSearchTerm
            }
          })
          .then((res) => res.data.property_groups)
    });

  // Check if partner is using Appfolio property groups
  const appfolioSettings = settingsData?.settings?.find(
    (s) => s.name === "appfolio_use_property_groups"
  );
  const isUsingAppfolioPropertyGroups =
    (appfolioSettings?.value as unknown) === true ||
    appfolioSettings?.value === "true";

  // Check if partner is using Propertyware property groups
  const propertywareSettings = settingsData?.settings?.find(
    (s) => s.name === "propertyware_use_property_groups"
  );
  const isUsingPropertywarePropertyGroups =
    (propertywareSettings?.value as unknown) === true ||
    propertywareSettings?.value === "true";

  const isUsingRentvinePropertyGroups =
    integrationCredentialsData?.integration_credentials.some((credential) => {
      const usePropertyGroups = credential.settings?.[
        "rentvine_use_property_groups"
      ] as unknown;

      return (
        !credential.soft_deleted_at &&
        credential.integration === PropertyManagementSystem.Rentvine &&
        (usePropertyGroups === true || usePropertyGroups === "true")
      );
    }) ?? false;

  const hasResolvedIntegrationCredentials =
    integrationCredentialsData !== undefined && !isIntegrationCredentialsError;
  const isUsingExternalPropertyGroups =
    !hasResolvedIntegrationCredentials ||
    Boolean(isUsingAppfolioPropertyGroups) ||
    isUsingPropertywarePropertyGroups ||
    isUsingRentvinePropertyGroups;

  const canManageGroups = adminPrivileges && !isUsingExternalPropertyGroups;

  if (isLoadingConfig || isLoadingIntegrationCredentials)
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <Loader />
      </div>
    );

  if (propertyGroups?.length === 0 && !debouncedSearchTerm && canManageGroups)
    return <EmptyPropertyGroups />;

  const totalProperties = propertyGroups?.reduce(
    (acc, curr) => acc + curr.total_properties,
    0
  );
  const showAppfolioAdminMessage =
    isUsingAppfolioPropertyGroups && adminPrivileges;
  const showPropertywareAdminMessage =
    isUsingPropertywarePropertyGroups && adminPrivileges;
  const showRentvineAdminMessage =
    isUsingRentvinePropertyGroups && adminPrivileges;

  return (
    <Layout sectionClassName="space-y-4 h-full">
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold">{dictionary.Property} groups</h1>
          <p className="text-sm text-content-400">
            {totalProperties} {dictionary.properties}
            <Show if={isUsingAppfolioPropertyGroups}>
              <span className="ml-2 text-xs text-content-500">
                (Managed by Appfolio)
              </span>
            </Show>
            <Show if={isUsingPropertywarePropertyGroups}>
              <span className="ml-2 text-xs text-content-500">
                (Managed by Propertyware)
              </span>
            </Show>
            <Show if={isUsingRentvinePropertyGroups}>
              <span className="ml-2 text-xs text-content-500">
                (Managed by Rentvine)
              </span>
            </Show>
          </p>
        </div>
        <Show if={canManageGroups}>
          <div className="flex gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              iconPosition="right"
              onClick={() => {
                navigate(
                  `/property-groups/configuration?step=${config?.split_by[0]}&edit=true`
                );
              }}
            >
              Edit groups
            </Button>

            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={ArrowSplit}
              iconPosition="right"
              onClick={() => setIsRegroupModalOpen(true)}
            >
              Regroup {dictionary.properties}
            </Button>
          </div>
        </Show>
        <Show if={showAppfolioAdminMessage}>
          <div className="text-sm text-content-500">
            <p>Property groups are managed by Appfolio integration.</p>
            <p>
              Configure group selection in Settings → Integration → Appfolio.
            </p>
          </div>
        </Show>
        <Show if={showPropertywareAdminMessage}>
          <div className="text-sm text-content-500">
            <p>Property groups are managed by Propertyware integration.</p>
            <p>Configure in Settings → Integration → Propertyware.</p>
          </div>
        </Show>
        <Show if={showRentvineAdminMessage}>
          <div className="text-sm text-content-500">
            <p>Property groups are managed by Rentvine integration.</p>
            <p>
              Configure group selection in Settings → Integration → Rentvine.
            </p>
          </div>
        </Show>
      </div>

      <Show if={isIntegrationCredentialsError}>
        <div role="alert" className="text-sm text-danger-500">
          Group editing is unavailable because integration settings could not be
          loaded.
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            onClick={() => refetchIntegrationCredentials()}
          >
            Try again
          </Button>
        </div>
      </Show>

      <div className="max-w-[271px] pt-2">
        <BaseInput
          id="search"
          autoComplete="off"
          placeholder="Search group"
          size={InputSize.SM}
          value={searchParams.search}
          onChange={(value) =>
            setSearchParams({
              ...searchParams,
              search: value
            })
          }
        />
      </div>
      <PropertyGroupsTable
        availableSplitOptions={config?.split_by || []}
        propertyGroups={propertyGroups || []}
        searchTerm={searchParams.search}
        isLoading={isFetchingPropertyGroups}
        isUsingExternalPropertyGroups={isUsingExternalPropertyGroups}
      />

      <Show if={isRegroupModalOpen}>
        <RegroupWarningModal
          isOpen={isRegroupModalOpen}
          onClose={() => setIsRegroupModalOpen(false)}
        />
      </Show>
    </Layout>
  );
};
